import {
  applyCellFormat,
  applyCellStyles,
  applyHeaderAlignment,
} from "./cellStyles";
import {
  applyColumnWidths,
  getCellWidthLength,
  getHeaderWidthLength,
  recordColumnWidth,
} from "./columnWidth";
import {
  EXPORT_COL_OFFSET,
  EXPORT_ROW_OFFSET,
  GUTTER_COLUMN_WIDTH,
} from "./constants";
import {
  applyTableBorders,
  getHeaderRows,
  getLastUsedColumn,
  getSectionBorderColumns,
  getTopLevelHeaderRow,
  offsetSectionBorderColumns,
} from "./sectionBorders";
import { exportCellValue } from "./values";

import {
  setPageFooterNames,
  setPageFooterFilters,
  setPageFooterDateFilters,
  setPageFooterCurrentDateTime,
} from "./applyPageInformation";

/**
 * Writes thead rows into the worksheet (values, merges, styles, column widths).
 */
export function setHeaders(table, worksheet, maxWidthByCol) {
  const theadRows = Array.from(table.querySelectorAll("thead tr"));
  const topLevelHeaderRow = getTopLevelHeaderRow(getHeaderRows(table));

  theadRows.forEach((tr, rowIndex) => {
    const ths = Array.from(tr.querySelectorAll("th"));
    let colIndex = 1 + EXPORT_COL_OFFSET;

    ths.forEach((th) => {
      const value = exportCellValue(th.innerText);
      const colspan = th.colSpan || 1;
      const startColIndex = colIndex;
      const excelRowIndex = rowIndex + 1 + EXPORT_ROW_OFFSET;

      const row = worksheet.getRow(excelRowIndex);
      const excelCell = row.getCell(startColIndex);
      excelCell.value = value;

      if (colspan > 1) {
        worksheet.mergeCells(
          excelRowIndex,
          startColIndex,
          excelRowIndex,
          startColIndex + colspan - 1
        );
      }

      applyCellStyles(excelCell, th, false);
      applyHeaderAlignment(excelCell, th, tr === topLevelHeaderRow);

      if (colspan === 1) {
        recordColumnWidth(
          maxWidthByCol,
          startColIndex,
          getHeaderWidthLength(excelCell, value)
        );
      }

      colIndex += colspan;
    });
  });
}

/**
 * Applies format, styles, and width tracking for a single body cell.
 */
function processBodyCell(cell, td, colNumber, maxWidthByCol) {
  const isNumberFormatCell = applyCellFormat(cell, td);
  applyCellStyles(cell, td, isNumberFormatCell);
  recordColumnWidth(maxWidthByCol, colNumber, getCellWidthLength(cell, td));
}

/**
 * Writes tbody rows into the worksheet (values, formats, styles, column widths).
 */
export function setRows(table, worksheet, maxWidthByCol) {
  const rows = table.querySelectorAll("tbody tr");

  rows.forEach((tr) => {
    const rowData = [
      // null, // TODO: uncomment when we confirm with client
      ...Array.from(tr.querySelectorAll("td")).map((td) =>
        exportCellValue(td.innerText)
      ),
    ];
    const excelRow = worksheet.addRow(rowData);

    excelRow.eachCell((cell, colNumber) => {
      if (colNumber <= EXPORT_COL_OFFSET) return;

      const td = tr.querySelector(
        `td:nth-child(${colNumber - EXPORT_COL_OFFSET})`
      );
      if (!td) return;

      processBodyCell(cell, td, colNumber, maxWidthByCol);
    });
  });
}

/**
 * Fills the worksheet from a cloned HTML table and applies column auto-width.
 */
export function populateWorksheet(
  table,
  worksheet,
  page,
  dateFilters,
  menuFilters,
  title,
  domain
) {
  const maxWidthByCol = new Map();

  setHeaders(table, worksheet, maxWidthByCol);
  setRows(table, worksheet, maxWidthByCol);

  // TODO: uncomment when we confirm with client
  // if (worksheet.rowCount > 0) {
  //   const topRow = 1 + EXPORT_ROW_OFFSET;
  //   const bottomRow = worksheet.rowCount;

  //   applyTableBorders(worksheet, {
  //     topRow,
  //     bottomRow,
  //     colStart: 1 + EXPORT_COL_OFFSET,
  //     colEnd: getLastUsedColumn(maxWidthByCol),
  //     sectionBorderColumns: offsetSectionBorderColumns(
  //       getSectionBorderColumns(table),
  //       EXPORT_COL_OFFSET
  //     ),
  //   });
  // }

  applyColumnWidths(worksheet, maxWidthByCol);

  // TODO: uncomment when we confirm with client
  // worksheet.getColumn(1).width = GUTTER_COLUMN_WIDTH;
  // setPageFooterNames(worksheet, "Page:", page?.displayName);
  // setPageFooterNames(worksheet, "Table:", title);
  // setPageFooterDateFilters(worksheet, dateFilters, page);
  // setPageFooterFilters(worksheet, page, menuFilters);
  // setPageFooterCurrentDateTime(worksheet, domain);

  return maxWidthByCol;
}
